import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { Toaster } from 'react-hot-toast'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      // gcTime must be >= persister maxAge so persisted queries are
      // still in the cache when rehydration runs.
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    },
  },
})

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'punchout-rq-cache',
  // Throttle writes to avoid hammering localStorage on every cache update
  throttleTime: 1000,
})

// Restrict what gets persisted. Photos and feed payloads can run into
// thousands of rows with long URLs — they'd blow the 5MB localStorage
// quota. Project lists, tag lists, etc. are small and safe.
const PERSIST_ALLOWLIST = new Set([
  'projects',          // active + inactive + inactive-count
  'tags',
  'project-permissions',
])

// Register service worker for PWA.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.log('SW registration failed:', error)
    })
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 1000 * 60 * 60 * 24 * 7, // 7 days
      // Bump this string to invalidate all persisted caches across users
      // (e.g. after a schema change to a cached query's row shape).
      buster: 'v1',
      dehydrateOptions: {
        shouldDehydrateQuery: query => {
          const root = query.queryKey[0]
          return typeof root === 'string' && PERSIST_ALLOWLIST.has(root)
        },
      },
    }}
  >
    <BrowserRouter>
      <App />
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#000000',
            color: '#fff',
          },
        }}
      />
    </BrowserRouter>
  </PersistQueryClientProvider>,
)
